// range/shape.ts - bucketedToShape(): collapse a BucketedRange (per-board bucket mass, from
// bucketRange()) into the coarse RangeShape the strategy/facts layers consume (ENGINE_DESIGN §4.4).
//
// StrategyContext.villain.rangeShape and EngineFacts.villain.rangeShape are BOTH RangeShape
// (valueHeavyPct / bluffPct / topClasses). Until now every caller fabricated that shape by hand - the
// S17 harness hardcodes per-archetype {valueHeavyPct, bluffPct} profiles (play-hand.ts villainProfileFor),
// which is the coarse-range simplification GH1 closes. This converter derives the shape from an ACTUAL
// bucketed (and, on the live path, per-street narrowed) range, so the villain's value/bluff split reflects
// the real board + the real narrowing, not a static guess.
//
// Mapping (deliberately simple + explainable - the coach shows this split):
//   valueHeavyPct = value + trap        (trap = slowplayed monsters; still value, just disguised)
//   bluffPct      = air + weakDraw       (non-made hands that are only ever bluffs when they bet)
//   showdown/strongDraw are the middle - neither pure value nor pure bluff - so they are intentionally
//   in NEITHER headline number (they are the bluff-catcher / semi-bluff region between the two poles).
// The two numbers do not sum to 100 by design; they are the two poles the bluff-catch math keys on.
import { RANGE_BUCKETS } from '@postflop/schema';
/** Human labels for each bucket, used to name the range's dominant components in topClasses. */
const BUCKET_LABEL = {
    value: 'strong made hands',
    trap: 'traps',
    strongDraw: 'strong draws',
    weakDraw: 'weak draws',
    showdown: 'bluff-catchers',
    air: 'air / busted draws',
};
/** How many dominant buckets to name in topClasses (highest range-share first). */
const TOP_CLASS_COUNT = 3;
/**
 * Collapse a bucketed range into a RangeShape. `valueHeavyPct` is the value + trap share; `bluffPct` is
 * the air + weak-draw share; `topClasses` names up to the three largest buckets by range share (dropping
 * empty ones). Pure; the input pcts are already range-relative (bucketRange divides by total mass), so a
 * fully-removed/empty range yields zeros and an empty topClasses.
 */
export const bucketedToShape = (bucketed) => {
    const pct = (b) => bucketed.buckets[b]?.pctOfRange ?? 0;
    const valueHeavyPct = clampPct(pct('value') + pct('trap'));
    const bluffPct = clampPct(pct('air') + pct('weakDraw'));
    const topClasses = [...RANGE_BUCKETS]
        .filter((b) => pct(b) > 0)
        .sort((a, b) => pct(b) - pct(a))
        .slice(0, TOP_CLASS_COUNT)
        .map((b) => BUCKET_LABEL[b]);
    return { valueHeavyPct, bluffPct, topClasses };
};
const clampPct = (x) => (x < 0 ? 0 : x > 100 ? 100 : Math.round(x * 100) / 100);
//# sourceMappingURL=shape.js.map